/* global document, window */

import * as React from 'react';
import '@fontsource/prompt/400.css';
import '@fontsource/prompt/500.css';
import '@fontsource/prompt/600.css';
import './lib/remixicon/remixicon.css';

import { BUILD_VERSION } from './src/generated/build-version';
import { MainLayout } from './src/layouts/main-layout';

const BUILD_RELOAD_STORAGE_KEY = 'gogeta-build-reload-key';
const BUILD_CHECK_INTERVAL_MS = 30000;

const canCheckBuildVersion = () =>
	typeof window !== 'undefined' && process.env.NODE_ENV === 'production';

const getBuildVersionUrl = () => {
	const pathPrefix = typeof __PATH_PREFIX__ !== 'undefined' ? __PATH_PREFIX__ : '';
	const basePath = pathPrefix || '';

	return `${basePath}/build-version.json?ts=${Date.now()}`;
};

const checkForUpdatedBuild = async () => {
	if (!canCheckBuildVersion() || typeof fetch !== 'function') {
		return;
	}

	try {
		const response = await fetch(getBuildVersionUrl(), {
			cache: 'no-store',
			credentials: 'same-origin',
		});

		if (!response.ok) {
			return;
		}

		const data = await response.json();
		const deployedBuildVersion = data?.buildVersion;

		if (!deployedBuildVersion || deployedBuildVersion === BUILD_VERSION) {
			window.sessionStorage.removeItem(BUILD_RELOAD_STORAGE_KEY);
			return;
		}

		const reloadKey = `${window.location.pathname}${window.location.search}${window.location.hash}:${deployedBuildVersion}`;
		if (window.sessionStorage.getItem(BUILD_RELOAD_STORAGE_KEY) === reloadKey) {
			return;
		}

		window.sessionStorage.setItem(BUILD_RELOAD_STORAGE_KEY, reloadKey);
		window.location.reload();
	} catch {
		// Ignore transient network/cache errors and try again on the next check.
	}
};

export const onClientEntry = () => {
	if (!canCheckBuildVersion()) {
		return;
	}

	const runBuildCheck = () => {
		void checkForUpdatedBuild();
	};

	window.addEventListener('focus', runBuildCheck);
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible') {
			runBuildCheck();
		}
	});
	window.setInterval(runBuildCheck, BUILD_CHECK_INTERVAL_MS);
};

export const onRouteUpdate = ({ location, prevLocation }) => {
	if (prevLocation === null || location.pathname === prevLocation.pathname) {
		if (!prevLocation && !location.hash) {
			// do not restore scroll position on page refresh without hash
			setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' }), 0);
		}
	}
};

export const shouldUpdateScroll = ({ routerProps: { location }, getSavedScrollPosition }) => {
	window.history.scrollRestoration = 'manual';
	const currentPosition = getSavedScrollPosition(location) || [0, 0];

	setTimeout(() => {
		if (location.hash) {
			// If hash (`/#something`), smooth scroll to that position
			const item = document.querySelector(`${location.hash}`);
			item?.scrollIntoView({ behavior: 'smooth' });
		} else {
			// when using the browser back/forward buttons or navigating to a different page client-side, instant scroll to its last saved position
			window.scrollTo({
				top: currentPosition[1],
				left: currentPosition[0],
				behavior: 'instant',
			});
		}
	}, 0);

	// do not let gatsby do anything more
	return false;
};

export const wrapPageElement = ({ element }) => {
	return <MainLayout>{element}</MainLayout>;
};
